import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'react-bootstrap';
import { useAppSelector } from '../../../../../hooks/redux';
import { ModalBox, RoundedAvatar } from '../../../../../components';
import Select from 'react-select';
import { customStyles } from '../../../../../components/modal/MemberModal';
import { IMatch } from '../PlayTag/PlayTag';
import { Team } from '../../../../../types/tournament';
import { imgOptionTeamLabel } from '../../../../../utils/imgOptionTeamLabel';
import { ClassFormatEnum, ClassTypeEnum } from '../../../../../types/enums';
import { isString } from '../../../../../utils/common.helpers';

const oppositeSideTeam = {
    homeTeam: 'awayTeam',
    awayTeam: 'homeTeam'
} as const;

const sideToPlayersRelation = {
    homeTeam: ['homePlayer1', 'homePlayer2'],
    awayTeam: ['awayPlayer1', 'awayPlayer2'],
} as const;

// TO DO: Remade this modal to one of the options - use store or reduce logic as much as possible
export const MatchTeamModal = (props: ModalProps) => {
    const { t } = useTranslation();
    
    const dontKnowText = t('select-dontKnow');

    const dontKnowTeamYet = useMemo(() => ({
        value: 0,
        label: dontKnowText,
        logo: null,
        id: 0,
        name: dontKnowText
    }), [dontKnowText]);

    const { classCollection, currentClassId } = useAppSelector((state) => state.class);
    const { matches, setMatches, matchId, fieldName, bracketCellId } = useAppSelector(
        (state) => state.modals.data
    );
    const side = fieldName as 'homeTeam' | 'awayTeam';
    const oppositeSide = oppositeSideTeam[side];
    const matchesList = Object.values(matches as Record<string, IMatch>) as IMatch[];
    const currentMatch = matchesList.find((m) => m.id === matchId);
    const bracketCellAnchor = bracketCellId
        ? matchesList.find((m) => m.bracketCellId === bracketCellId)
        : null;
    const oppositeTeam = bracketCellId && bracketCellAnchor
        ? bracketCellAnchor[oppositeSide]
        : currentMatch?.[oppositeSide];
    const oppositeSideId =
        typeof oppositeTeam === 'object' && oppositeTeam != null
            ? oppositeTeam.id
            : oppositeTeam;
    const [selections, setSelections] = useState<Team>({} as Team);
    const classData = classCollection.find((el) => el.classId === currentClassId);
    const teamOptions = [dontKnowTeamYet, ...(classData?.teams.filter( item => {
        return oppositeSideId !== item.id;
    }).map((team) => imgOptionTeamLabel(team)) || [])];
    const tournamentMatches = matchesList.map((match) => {
        if (bracketCellId) {
            if (match.bracketCellId !== bracketCellId) {
                return match;
            }
            const updated: IMatch = {
                ...match,
                [side]: selections.id ? selections : null,
                homePlayer1: null,
                homePlayer2: null,
                awayPlayer1: null,
                awayPlayer2: null,
            };
            return updated;
        }
        return match.id === matchId ? { ...match, [side]: selections } : match;
    });
    const prefix = 'modal-';
    const inputPrefix = 'input-';

    const handleSelect = useCallback(
        (option: { value: string | number; label: string }) => {
            let team;
            if(option.value === dontKnowTeamYet.value) {
                team = Object.assign({}, dontKnowTeamYet);
            } else {
                team = classData?.teams.find((team) => team.id === option.value);
            }

            typeof team?.id === 'number' &&
                setSelections({ id: team.id, logo: team.logo, name: team.name });
        },
        [classData, dontKnowTeamYet]
    );

    const handleSave = () => {
        const isBoxLeague = classData?.format === ClassFormatEnum.BOX_LEAGUE || 
                            classData?.format === ClassFormatEnum.BOX_LEAGUE_ELIM;
        setMatches(
            'tournamentMatches',
            tournamentMatches.reduce((acc: Record<string, IMatch>, item: IMatch) => {
                const draft = { ...item };
                const shouldApplyPlayers = !bracketCellId && draft.id === matchId;
                if (shouldApplyPlayers) {
                    sideToPlayersRelation[side]?.forEach((fieldToChange, index) => {
                        const user = classData?.teams.find(team => team.id === selections.id)?.users?.[index] || null;

                        if(ClassTypeEnum.DOUBLES === classData?.type || ClassTypeEnum.TEAMS === classData?.type || 
                            (ClassTypeEnum.SINGLES && isBoxLeague) ||  index === 0) {
                            draft[fieldToChange] = user;
                        } else {
                            draft[fieldToChange] = null;
                        }
                    });

                    if(!draft[side]?.id) {
                        const placeholderKey = `${side}Placeholder` as 'homeTeamPlaceholder' | 'awayTeamPlaceholder';
                        if(!isString(draft[placeholderKey])) {
                            draft[placeholderKey] = '';
                        }
                        draft[side] = null;
                    }
                }
                acc[`${draft.id}`] = draft;
                return acc;
            }, {})
        );
        props.onHide && props.onHide();
    };

    return (
        <ModalBox {...props} title={t(prefix + 'addTeam')} handleSave={handleSave}>
            <Select
                styles={customStyles}
                name="matchTeam"
                placeholder={t(inputPrefix + 'placeholderSelect')}
                options={teamOptions}
                className="basic-single holder-select"
                classNamePrefix="select"
                // @ts-ignore
                onChange={handleSelect}
                formatOptionLabel={(data: any) => (
                    <div className="d-flex align-items-center">
                        {(data.logo && (<RoundedAvatar
                        name="flag"
                        avatar={data.logo}
                        alt="Flag image"
                    />)) || (<div className="raqt__seed-holder">
                                {data.seed}
                            </div>)}
                        {data.label}
                    </div>
                )}
            />
        </ModalBox>
    );
};
